import { useContext, type PropsWithChildren } from 'react';

import type { IconComponent } from '@repo/ui-icons';

import type { BaseComponentProps } from '../../../../types/common';
import { H5, H6 } from '../../Typography';

import { StandardCardSharedPropsContext } from './context';
import styles from './StandardCard.module.css';
import type { StandardCardProps } from './types';

export interface StandardCardContentTitleProps extends BaseComponentProps {
  /**
   * The icon to display in the title.
   */
  icon?: IconComponent;

  /**
   * The title to display.
   */
  title: string;

  /**
   * Whether the title should be truncated.
   */
  textOverflowEllipsis?: boolean;

  /**
   * The children of the ContentTitle component (rendered on the right side of the title).
   */
  children?: PropsWithChildren['children'];
}

const getTitleTypography = (layout: StandardCardProps['layout']) => {
  switch (layout) {
    case 'horizontal':
      return H6;
    case 'square':
    default:
      return H5;
  }
};

export const ContentTitle: React.FC<StandardCardContentTitleProps> = ({
  children,
  icon: Icon,
  title,
  textOverflowEllipsis = true,
}) => {
  const { layout } = useContext(StandardCardSharedPropsContext);
  const Title = getTitleTypography(layout);

  return (
    <div className={styles['standard-card-title-wrapper']}>
      <span className={styles['standard-card-title']}>
        {Icon && <Icon color="currentColor" size={16} />}
        <Title bold color="inherit" textOverflowEllipsis={textOverflowEllipsis}>
          {title}
        </Title>
      </span>
      {children}
    </div>
  );
};
